// The nav Views list ordering - pure, so it unit-tests without dragging in the
// app-shell client component. app-shell.tsx owns the localStorage/window glue
// (read/subscribe/set) and re-uses these constants + the sort function.
//
// A personal, client-only preference: it never leaves the browser, so it rides
// localStorage, not the log. `viewed` (most-recently-opened by me, the DEFAULT) is
// backed by the private per-person view-open mark (src/lib/view-opens.ts), fed in
// as `lastViewedAt`; `recent` = newest created; `changed` = most-recently-edited
// lens; `type` orders boards by their layout (kanban, list, checklist), then
// views; `alpha` is A-Z. Views the viewer has never opened have no mark, so
// under `viewed` they sink below the opened ones and fall back to recency then
// name.

import { BOARD_LAYOUTS, isBoardLayout } from '@/domain/board-view';
import type { LensRail } from '@/domain/lens-view';
import type { PlainKey } from '@/i18n';

export type ViewsSort = 'viewed' | 'recent' | 'changed' | 'type' | 'alpha';

export const VIEWS_SORTS: ViewsSort[] = ['viewed', 'recent', 'changed', 'type', 'alpha'];

export const DEFAULT_VIEWS_SORT: ViewsSort = 'viewed';

/** Each sort's name, as a catalog key the surface formats (docs/I18N.md). */
export const VIEWS_SORT_LABEL: Record<ViewsSort, PlainKey> = {
  viewed: 'shell.viewsSort.viewed',
  recent: 'shell.viewsSort.recent',
  changed: 'shell.viewsSort.changed',
  type: 'shell.viewsSort.type',
  alpha: 'shell.viewsSort.alpha',
};

/** The subset of a nav view the ordering reads. `NavView` (app-shell.tsx) is a
 *  superset, so it satisfies this structurally. `lens` rows sort with the same
 *  vocabulary (their section is single-kind, so 'type' degrades to A-Z). */
export interface NavViewOrder {
  name: string;
  kind: 'board' | 'lens';
  /** A board's default layout, which the 'type' sort orders boards by. A
   *  lens's rail does not rank it, and the sidebar passes null for a lens. */
  layout: LensRail | null;
  /** epoch ms */
  createdAt: number;
  /** epoch ms */
  changedAt: number;
  /** epoch ms of the viewer's last open, or null if never opened. */
  lastViewedAt: number | null;
}

/** The 'type' sort's rank: a board by its default layout, in the layouts'
 *  order (kanban, list, checklist), then every lens; lenses live in their own
 *  nav section, so their rank only matters inside Pinned. */
function typeRank(view: NavViewOrder): number {
  return view.kind === 'board' && isBoardLayout(view.layout)
    ? BOARD_LAYOUTS.indexOf(view.layout)
    : BOARD_LAYOUTS.length;
}

/** Order a copy of the nav Views list by the chosen key. Pure; ties break on name
 *  so the order is stable. */
export function sortNavViews<T extends NavViewOrder>(views: T[], sort: ViewsSort): T[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  return [...views].sort((a, b) => {
    switch (sort) {
      case 'alpha':
        return byName(a, b);
      case 'changed':
        return b.changedAt - a.changedAt || byName(a, b);
      case 'type':
        // Boards by layout, then lenses; A-Z within each.
        return typeRank(a) - typeRank(b) || byName(a, b);
      case 'viewed': {
        // Most-recently-opened by me first. A never-opened view (null mark) has
        // no place in the recency order, so it sinks below every opened one and
        // falls back to recency (then name) among its own kind.
        const av = a.lastViewedAt;
        const bv = b.lastViewedAt;
        if (av !== null && bv !== null) return bv - av || byName(a, b);
        if (av !== null) return -1;
        if (bv !== null) return 1;
        return b.createdAt - a.createdAt || byName(a, b);
      }
      case 'recent':
      default:
        return b.createdAt - a.createdAt || byName(a, b);
    }
  });
}
